import React, { useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase/firebase";
import { toast } from "react-toastify";
import axios from "axios";
import { DEV } from "../../services/constants";
import ReceiverSelect from "../../components/customer/ReceiverSelect";
import useViewOnly from "../../hooks/useViewOnly";
import styles from "./handoverGroupModal.module.scss";

const TABS = [
    { key: "packages", label: "Packages", icon: "pi pi-box" },
    { key: "receiver", label: "Receiver", icon: "pi pi-user" },
    { key: "photos", label: "Photos", icon: "pi pi-camera" },
    { key: "complete", label: "Complete", icon: "pi pi-check" },
];

const HandoverGroupModal = ({ visible, onHide, handoverId, group, onSuccess }) => {
    const [activeTab, setActiveTab] = useState("packages");
    const [receiverState, setReceiverState] = useState({ receiver: null, signatureDataUrl: null, isValid: false });
    const [photos, setPhotos] = useState([null, null]);
    const [activeSlot, setActiveSlot] = useState(null);
    const [cameraActive, setCameraActive] = useState(false);
    const [stream, setStream] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [resolving, setResolving] = useState(false);
    const isAdmin = localStorage.getItem("role") === "admin";
    const viewOnly = useViewOnly();
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const fileInputRef = useRef(null);

    // Reset local state whenever a new group is opened
    useEffect(() => {
        if (visible) {
            setActiveTab("packages");
            setReceiverState({ receiver: null, signatureDataUrl: null, isValid: false });
            setPhotos([null, null]);
            setActiveSlot(null);
            setCameraActive(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, group?._id]);

    useEffect(() => {
        return () => {
            if (stream) stream.getTracks().forEach((track) => track.stop());
        };
    }, [stream]);

    // Only acquire/attach the camera once cameraActive flips to true and the
    // <video> element has actually mounted - doing this inside startCamera()
    // itself was too early (the element is conditionally rendered on
    // cameraActive, so the ref was still null at that point).
    useEffect(() => {
        if (!cameraActive) return;
        let cancelled = false;

        navigator.mediaDevices
            .getUserMedia({
                video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
                audio: false,
            })
            .then(async (mediaStream) => {
                if (cancelled) {
                    mediaStream.getTracks().forEach((track) => track.stop());
                    return;
                }
                if (videoRef.current) {
                    videoRef.current.srcObject = mediaStream;
                    await videoRef.current.play();
                }
                setStream(mediaStream);
            })
            .catch((error) => {
                console.error("Camera error:", error);
                toast.error("Unable to access camera. Use upload instead.");
                setCameraActive(false);
                setActiveSlot(null);
            });

        return () => {
            cancelled = true;
        };
    }, [cameraActive]);

    if (!group) return null;

    const isHandedOver = group.status === "HandedOver";
    const needsReview = group.processingStatus === "needsReview";
    const hasUnpaid = group.packages?.some((p) => !p.isPaidSnapshot);
    const receiverDone = receiverState.isValid;
    const photosDone = !!photos[0] && !!photos[1];

    const getProgress = () => {
        let progress = 10;
        if (receiverDone) progress += 45;
        if (photosDone) progress += 45;
        return Math.min(progress, 100);
    };

    const startCamera = (slotIndex) => {
        setActiveSlot(slotIndex);
        setCameraActive(true);
    };

    const stopCamera = () => {
        if (stream) stream.getTracks().forEach((track) => track.stop());
        setStream(null);
        setCameraActive(false);
        setActiveSlot(null);
    };

    const capturePhoto = () => {
        if (!videoRef.current || !canvasRef.current || activeSlot === null) return;
        const video = videoRef.current;
        const canvas = canvasRef.current;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext("2d").drawImage(video, 0, 0);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        setPhotos((prev) => {
            const next = [...prev];
            next[activeSlot] = dataUrl;
            return next;
        });
        stopCamera();
    };

    const handleFileUpload = (slotIndex, file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = () => {
            setPhotos((prev) => {
                const next = [...prev];
                next[slotIndex] = reader.result;
                return next;
            });
        };
        reader.readAsDataURL(file);
    };

    const uploadDataUrl = async (dataUrl, path) => {
        const blob = await fetch(dataUrl).then((r) => r.blob());
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, blob);
        return getDownloadURL(storageRef);
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const timestamp = Date.now();
            const [signatureUrl, photo1Url, photo2Url] = await Promise.all([
                uploadDataUrl(receiverState.signatureDataUrl, `airportHandovers/${handoverId}/groups/${group._id}/signature_${timestamp}.png`),
                uploadDataUrl(photos[0], `airportHandovers/${handoverId}/groups/${group._id}/photo1_${timestamp}.jpg`),
                uploadDataUrl(photos[1], `airportHandovers/${handoverId}/groups/${group._id}/photo2_${timestamp}.jpg`),
            ]);

            const response = await axios.post(
                `${DEV}/airport-handover/${handoverId}/group/${group._id}/handover`,
                { receiver: receiverState.receiver, signatureUrl, photoUrls: [photo1Url, photo2Url] },
                { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
            );

            if (response?.data?.message === "Invalid token") {
                toast.error("session expired");
                localStorage.removeItem("token");
                window.location.href = "/login";
                return;
            }

            if (response?.data?.success) {
                toast.success("Handed over successfully");
                (response.data.data.warnings || []).forEach((w) => toast.warn(w));
                if (onSuccess) onSuccess(response.data.data.group);
                onHide();
            } else {
                toast.error(response?.data?.message || "Failed to submit handover");
            }
        } catch (error) {
            console.error("Error submitting group handover:", error);
            toast.error(error?.response?.data?.message || "Failed to submit handover");
            if (error?.response?.status === 401) {
                localStorage.removeItem("token");
                window.location.href = "/login";
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleResolve = async () => {
        setResolving(true);
        try {
            const response = await axios.post(
                `${DEV}/airport-handover/${handoverId}/group/${group._id}/resolve`,
                {},
                { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
            );
            if (response?.data?.success) {
                toast.success("Resolved - split into separate client handovers");
                const data = response.data.data;
                if (data?.unresolvedPackageIds?.length) {
                    toast.warn(`${data.unresolvedPackageIds.length} package(s) had no identifiable client and were left unchanged.`);
                }
                if (data?.expectedPackageUpdateCount !== data?.actualPackageUpdateCount) {
                    toast.warn(
                        `Only ${data.actualPackageUpdateCount} of ${data.expectedPackageUpdateCount} package statuses actually updated - check the affected packages.`,
                    );
                }
                if (onSuccess) onSuccess();
                onHide();
            } else {
                toast.error(response?.data?.message || "Failed to resolve");
            }
        } catch (error) {
            console.error("Error resolving needs-review group:", error);
            toast.error(error?.response?.data?.message || "Failed to resolve");
        } finally {
            setResolving(false);
        }
    };

    // Group the matched packages by their resolved owning client, so the
    // admin can see exactly what the split will look like before resolving.
    const reviewBreakdown = (() => {
        const byClient = new Map();
        (group?.packages || []).forEach((p) => {
            const pkg = p.packageId;
            const clientName = pkg?.name ? `${pkg.name.firstName} ${pkg.name.lastName}` : "Unknown client";
            if (!byClient.has(clientName)) byClient.set(clientName, []);
            byClient.get(clientName).push(pkg?.Waybill || pkg?._id || pkg);
        });
        return Array.from(byClient.entries());
    })();

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            className={styles.handoverDialog}
            draggable={false}
            dismissableMask
            blockScroll
            header={
                <div className={styles.dialogHeader}>
                    <div>
                        <h3>{needsReview ? "Needs Review" : group.clientName}</h3>
                        <p>{group.packages?.length || 0} package(s)</p>
                    </div>
                    <div className={styles.countBadge}>{needsReview ? "Needs Review" : group.status}</div>
                </div>
            }
        >
            <div className={styles.dialogContent}>
                {needsReview && (
                    <div>
                        <div className={styles.warningBanner}>
                            <i className="pi pi-exclamation-triangle" />
                            This batch's photos matched packages belonging to more than one client. It hasn't been handed over or notified to anyone yet.
                        </div>

                        <div className={styles.sectionHeader}>
                            <h4>
                                <i className="pi pi-sitemap" />
                                Detected split
                            </h4>
                            <p>If this looks right, Resolve will hand over each client's packages separately and notify them. Otherwise close this and re-submit the photos correctly split up.</p>
                        </div>
                        <div className={styles.packageList}>
                            {reviewBreakdown.map(([clientName, waybills]) => (
                                <div key={clientName} className={styles.packageItem}>
                                    <strong>{clientName}</strong>
                                    <span>{waybills.join(", ")}</span>
                                </div>
                            ))}
                        </div>

                        <div className={styles.sectionHeader}>
                            <h4>
                                <i className="pi pi-camera" />
                                Label Photos
                            </h4>
                        </div>
                        <div className={styles.photoSlots} style={{ gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))" }}>
                            {(group.photoUrls || []).map((url) => (
                                <div key={url} className={styles.photoSlot}>
                                    <img src={url} alt="Label" />
                                </div>
                            ))}
                        </div>

                        <div className={styles.navRow}>
                            <Button label="Close" icon="pi pi-times" outlined onClick={onHide} />
                            {isAdmin && (
                                <Button
                                    label={resolving ? "Resolving..." : "Resolve - Split by Client"}
                                    icon={resolving ? "pi pi-spin pi-spinner" : "pi pi-check"}
                                    className={styles.submitButton}
                                    disabled={resolving}
                                    onClick={handleResolve}
                                />
                            )}
                        </div>
                    </div>
                )}

                {!needsReview && hasUnpaid && (
                    <div className={styles.warningBanner}>
                        <i className="pi pi-exclamation-triangle" />
                        One or more packages in this group are unpaid.
                        {!isHandedOver && " You can still complete the handover."}
                    </div>
                )}

                {!needsReview && (isHandedOver ? (
                    <div>
                        <div className={styles.sectionHeader}>
                            <h4>
                                <i className="pi pi-check-circle" />
                                Handover Record
                            </h4>
                            <p>
                                Handed over {group.handedOverAt ? new Date(group.handedOverAt).toLocaleString() : ""}
                                {group.handedOverBy ? ` by ${group.handedOverBy.firstName} ${group.handedOverBy.lastName}` : ""}
                            </p>
                            {group.capturedBy && group.capturedBy._id !== group.handedOverBy?._id && (
                                <p>
                                    Photos submitted by {group.capturedBy.firstName} {group.capturedBy.lastName}
                                </p>
                            )}
                        </div>

                        <div className={styles.packageList}>
                            {group.packages?.map((p) => (
                                <div key={p.packageId._id || p.packageId} className={styles.packageItem}>
                                    <strong>{p.packageId.Waybill || p.packageId}</strong>
                                    <span className={p.isPaidSnapshot ? styles.paidTag : styles.unpaidTag}>{p.isPaidSnapshot ? "Paid" : "Unpaid"}</span>
                                </div>
                            ))}
                        </div>

                        <div className={styles.reviewGrid}>
                            <div className={styles.reviewItem}>
                                <h5>
                                    <i className="pi pi-user" />
                                    Receiver
                                </h5>
                                <p>
                                    {group.receiver?.name || "-"}
                                    {group.receiver?.type ? ` (${group.receiver.type}${group.receiver.idLast6 ? `, ID ending ${group.receiver.idLast6}` : ""})` : ""}
                                </p>
                            </div>
                            {group.signatureUrl && (
                                <div className={styles.reviewItem}>
                                    <h5>
                                        <i className="pi pi-pencil" />
                                        Signature
                                    </h5>
                                    <img src={group.signatureUrl} alt="Signature" className={styles.reviewThumb} />
                                </div>
                            )}
                            {(group.photoUrls || []).map((url, i) => (
                                <div key={url} className={styles.reviewItem}>
                                    <h5>
                                        <i className="pi pi-camera" />
                                        Photo {i + 1}
                                    </h5>
                                    <img src={url} alt={`Proof ${i + 1}`} className={styles.reviewThumb} />
                                </div>
                            ))}
                        </div>

                        <div className={styles.navRow}>
                            <span />
                            <Button label="Close" icon="pi pi-times" outlined onClick={onHide} />
                        </div>
                    </div>
                ) : (
                    <>
                <div className={styles.progressSection}>
                    <div className={styles.progressBar}>
                        <div className={styles.progressFill} style={{ width: `${getProgress()}%` }} />
                    </div>
                    <p className={styles.progressText}>{getProgress()}% Complete</p>
                </div>

                <div className={styles.tabs}>
                    {TABS.map((tab) => (
                        <button
                            key={tab.key}
                            className={`${styles.tab} ${activeTab === tab.key ? styles.active : ""} ${
                                (tab.key === "receiver" && receiverDone) || (tab.key === "photos" && photosDone) ? styles.completed : ""
                            }`}
                            onClick={() => setActiveTab(tab.key)}
                            disabled={tab.key === "complete" && (!receiverDone || !photosDone)}
                        >
                            <i className={tab.icon} />
                            {tab.label}
                            {((tab.key === "receiver" && receiverDone) || (tab.key === "photos" && photosDone)) && <i className="pi pi-check-circle" />}
                        </button>
                    ))}
                </div>

                <div className={styles.tabContent}>
                    {activeTab === "packages" && (
                        <div>
                            <div className={styles.sectionHeader}>
                                <h4>
                                    <i className="pi pi-box" />
                                    Packages in this group
                                </h4>
                                <p>Confirm these are the packages being handed over.</p>
                            </div>
                            <div className={styles.packageList}>
                                {group.packages?.map((p) => (
                                    <div key={p.packageId._id || p.packageId} className={styles.packageItem}>
                                        <strong>{p.packageId.Waybill || p.packageId}</strong>
                                        <span className={p.isPaidSnapshot ? styles.paidTag : styles.unpaidTag}>{p.isPaidSnapshot ? "Paid" : "Unpaid"}</span>
                                    </div>
                                ))}
                            </div>
                            <div className={styles.navRow}>
                                <span />
                                <Button label="Next: Receiver" icon="pi pi-arrow-right" iconPos="right" className={styles.nextButton} onClick={() => setActiveTab("receiver")} />
                            </div>
                        </div>
                    )}

                    {activeTab === "receiver" && (
                        <div>
                            <div className={styles.sectionHeader}>
                                <h4>
                                    <i className="pi pi-user" />
                                    Who is receiving this?
                                </h4>
                                <p>Select the receiver and capture their signature.</p>
                            </div>
                            <div className={styles.receiverStep}>
                                <ReceiverSelect clientId={group.clientId?._id || group.clientId} clientName={group.clientName} onChange={setReceiverState} />
                            </div>
                            <div className={styles.navRow}>
                                <Button label="Back" icon="pi pi-arrow-left" outlined onClick={() => setActiveTab("packages")} />
                                <Button label="Next: Photos" icon="pi pi-arrow-right" iconPos="right" className={styles.nextButton} disabled={!receiverDone} onClick={() => setActiveTab("photos")} />
                            </div>
                        </div>
                    )}

                    {activeTab === "photos" && (
                        <div>
                            <div className={styles.sectionHeader}>
                                <h4>
                                    <i className="pi pi-camera" />
                                    Proof Photos
                                </h4>
                                <p>2 photos are required as proof of handover.</p>
                            </div>

                            {cameraActive ? (
                                <div className={styles.cameraContainer}>
                                    <video ref={videoRef} autoPlay playsInline muted className={styles.video} />
                                    <canvas ref={canvasRef} className={styles.hiddenCanvas} />
                                    <div className={styles.cameraControls}>
                                        <Button icon="pi pi-times" rounded severity="danger" onClick={stopCamera} tooltip="Cancel" />
                                        <Button icon="pi pi-circle" rounded className={styles.captureButton} onClick={capturePhoto} tooltip="Capture" />
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.photoSlots}>
                                    {[0, 1].map((index) => (
                                        <div key={index} className={styles.photoSlot}>
                                            {photos[index] ? (
                                                <>
                                                    <img src={photos[index]} alt={`Proof ${index + 1}`} />
                                                    <Button
                                                        icon="pi pi-refresh"
                                                        rounded
                                                        className={styles.retakeButton}
                                                        onClick={() =>
                                                            setPhotos((prev) => {
                                                                const next = [...prev];
                                                                next[index] = null;
                                                                return next;
                                                            })
                                                        }
                                                    />
                                                </>
                                            ) : (
                                                <div className={styles.emptySlot} onClick={() => startCamera(index)}>
                                                    <i className="pi pi-camera" />
                                                    <span>Photo {index + 1}</span>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {!cameraActive && (
                                <div className={styles.photoOptions}>
                                    <Button label="Upload Instead" icon="pi pi-upload" outlined onClick={() => fileInputRef.current?.click()} />
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        style={{ display: "none" }}
                                        onChange={(e) => {
                                            const emptyIndex = photos.findIndex((p) => !p);
                                            if (emptyIndex !== -1) handleFileUpload(emptyIndex, e.target.files[0]);
                                        }}
                                    />
                                </div>
                            )}

                            <div className={styles.navRow}>
                                <Button label="Back" icon="pi pi-arrow-left" outlined onClick={() => setActiveTab("receiver")} />
                                <Button label="Next: Complete" icon="pi pi-arrow-right" iconPos="right" className={styles.nextButton} disabled={!photosDone} onClick={() => setActiveTab("complete")} />
                            </div>
                        </div>
                    )}

                    {activeTab === "complete" && (
                        <div>
                            <div className={styles.sectionHeader}>
                                <h4>
                                    <i className="pi pi-check-circle" />
                                    Review &amp; Complete
                                </h4>
                                <p>Confirm everything before submitting.</p>
                            </div>
                            <div className={styles.reviewGrid}>
                                <div className={styles.reviewItem}>
                                    <h5>
                                        <i className="pi pi-user" />
                                        Receiver
                                    </h5>
                                    <p>{receiverState.receiver?.name}</p>
                                </div>
                                <div className={styles.reviewItem}>
                                    <h5>
                                        <i className="pi pi-pencil" />
                                        Signature
                                    </h5>
                                    <img src={receiverState.signatureDataUrl} alt="Signature" className={styles.reviewThumb} />
                                </div>
                                <div className={styles.reviewItem}>
                                    <h5>
                                        <i className="pi pi-camera" />
                                        Photo 1
                                    </h5>
                                    <img src={photos[0]} alt="Proof 1" className={styles.reviewThumb} />
                                </div>
                                <div className={styles.reviewItem}>
                                    <h5>
                                        <i className="pi pi-camera" />
                                        Photo 2
                                    </h5>
                                    <img src={photos[1]} alt="Proof 2" className={styles.reviewThumb} />
                                </div>
                            </div>
                            <div className={styles.navRow}>
                                <Button label="Back" icon="pi pi-arrow-left" outlined onClick={() => setActiveTab("photos")} />
                                <Button
                                    label={submitting ? "Submitting..." : "Complete Handover"}
                                    icon={submitting ? "pi pi-spin pi-spinner" : "pi pi-check"}
                                    className={styles.submitButton}
                                    disabled={submitting || viewOnly}
                                    onClick={handleSubmit}
                                />
                            </div>
                        </div>
                    )}
                </div>
                    </>
                ))}
            </div>
        </Dialog>
    );
};

export default HandoverGroupModal;
